import {dbUtil, storage} from '@/api/firebase';
import {Logger} from '@vanti/vue-logger';
import DeferUtil from './defer-util';
import Vue from 'vue';
import {CascadingConfig} from '@/utils/config';
import {config as impl} from '@/room-booking/impl';

const log = Logger.get('store/config');

export default {
  namespaced: true,
  state: {
    userConfigRef: null,
    userConfigNotLoadedErrorMessage: '',
    minorErrorMessage: '',
    configCache: {},
    styleUrl: '',
    backgroundUrl: ''
  },
  getters: {
    docRefsToWatch(state) {
      const refs = [];
      let head = state.userConfigRef;
      while (head) {
        refs.push(head);
        const doc = state.configCache[head.path];
        if (doc) {
          head = doc.ref;
        } else {
          break;
        }
      }
      return refs;
    },
    mergedConfig(state) {
      const docs = [];
      let head = state.userConfigRef;
      while (head) {
        const doc = state.configCache[head.path];
        if (doc) {
          docs.push({config: doc});
          head = doc.ref;
        } else {
          break;
        }
      }
      return new CascadingConfig(docs);
    },
    autoCheckOut(state, getters) {
      return !getters.readOnlyMode && getters.mergedConfig.get('autoCheckOut', false);
    },
    readOnlyMode(state, getters) {
      return getters.mergedConfig.get('readOnly', false);
    },
    style(state) {
      return state.styleUrl;
    },
    background(state) {
      return state.backgroundUrl;
    },
    panelType(state, getters) {
      return getters.mergedConfig.get('panelType');
    },
    panelApiPw(state, getters) {
      return getters.mergedConfig.get('panelApiPw');
    },
    panelLightsBrightness(state, getters) {
      return getters.mergedConfig.get('panelLightsBrightness', 1);
    },
    panelLightsCrestronEndpoint(state, getters) {
      return getters.mergedConfig.get('panelLightsCrestronEndpoint');
    },
    panelLightsCrestronRoomName(state, getters) {
      return getters.mergedConfig.get('panelLightsCrestronRoomName');
    },
    reloadTime(state, getters) {
      return getters.mergedConfig.get('reloadTime');
    },
    roomDisplayName(state, getters) {
      return getters.mergedConfig.get('displayName');
    },
    timeFormat(state, getters) {
      return getters.mergedConfig.get('timeFormat', 'p');
    },
    resourceRef(state, getters) {
      return getters.mergedConfig.get(impl.RESOURCE_REF_PROP);
    },
    hybridOfficeBookable(state, getters) {
      return getters.mergedConfig.get('hybridOfficeBookable');
    },
    configLoaded(state, getters) {
      return state.userConfigRef &&
          getters.docRefsToWatch.every(docRef => state.configCache.hasOwnProperty(docRef.path));
    },
    styleStoragePath(state, getters) {
      return getters.mergedConfig.get('cssFile', false);
    },
    backgroundStoragePath(state, getters) {
      return getters.mergedConfig.get('backgroundImage', false);
    }
  },
  mutations: {
    ...DeferUtil.mutations(log),
    updateCache(state, {ref, doc}) {
      const data = doc.hasOwnProperty(impl.PROFILE_PROP) ? doc[impl.PROFILE_PROP] : doc;
      Vue.set(state.configCache, ref.path, data);
    },
    cacheClear(state, keys) {
      for (const key of keys) {
        Vue.delete(state.configCache, key);
      }
    },
    setUserConfigRef(state, ref) {
      state.userConfigRef = ref;
    },
    setUserConfigNotLoadedErrorMessage(state, message) {
      state.userConfigNotLoadedErrorMessage = message;
    },
    setCssFile(state, cssFile) {
      log.debug('setCssFile', cssFile);
      state.styleUrl = cssFile;
    },
    setBackgroundFile(state, backgroundFile) {
      log.debug('setBackgroundFile', backgroundFile);
      state.backgroundUrl = backgroundFile;
    }
  },
  actions: {
    init: {
      root: true,
      handler({commit, state, getters, dispatch, rootGetters}) {
        // watch top level config doc
        this.watch(
            () => rootGetters['auth/authUserUid'],
            async uid => {
              if (uid) {
                const ref = await dbUtil.doc('users', uid);

                /* Fetch the email from the authUser that logged in, because if the user doc cannot be found,
                then we can't access data from that doc such as the email in order to send useful error messages */
                const authUserEmail = rootGetters['auth/authUser'].email;
                log.info(`authUserEmail: ${authUserEmail}`);

                commit('setUserConfigRef', ref);
                commit('defer', {
                  [ref.path]: ref.onSnapshot(
                      doc => {
                        if (doc.exists) {
                          commit('updateCache', {ref, doc: doc.data()});
                        } else {
                          log.error(`User configuration could not ` +
                            `be loaded for user ID ${ref.path} with email ${authUserEmail}.`);

                          // Send a message that can be used on the frontend to alert the user
                          commit('setUserConfigNotLoadedErrorMessage', `User configuration could not ` +
                              `be loaded for user ${authUserEmail}. User not found.`);
                        }
                      },
                      err => {
                        log.error(`init authUserUid ${ref.path}.onSnapshot`, err);
                        commit('setUserConfigNotLoadedErrorMessage', `User configuration could not ` +
                          `be loaded for user ${authUserEmail}. Error.`);
                      })
                });
              } else {
                dispatch('unbind');
              }
            },
            {immediate: true}
        );

        // add or remove watchers on config docs
        this.watch(
            () => getters.docRefsToWatch,
            docRefs => {
              const deleted = {...(state.defer || {})};
              const added = [];

              // remove all all refs we watch to keep watching or add refs we don't know about to added.
              for (const ref of docRefs) {
                if (deleted.hasOwnProperty(ref.path)) {
                  delete deleted[ref.path];
                } else {
                  added.push(ref);
                }
              }

              // stop listening to docs we no longer need to
              const keys = Object.keys(deleted);
              if (keys.length > 0) {
                commit('reset', keys);
                commit('cacheClear', keys);
              }

              // start listening to new refs
              for (const ref of added) {
                commit('defer', {
                  [ref.path]: ref.onSnapshot(
                      doc => {
                        if (doc.exists) {
                          commit('updateCache', {ref, doc: doc.data()});
                          log.info('Updating cache for doc');
                        } else {
                          log.error('Couldn\'t find doc');
                        }
                      },
                      err => {
                        log.error(`init docRefsToWatch ${ref.path}.onSnapshot`, err);
                      })
                });
              }
            });

        this.watch(
            () => getters.styleStoragePath,
            url => {
              if (url) {
                // fetch custom css from storage
                storage.then(s => s.ref().child(url).getDownloadURL())
                    .then(url => {
                      commit('setCssFile', url);
                    })
                    .catch(err => {
                      log.error('error getting css URL', err);
                      commit('setMinorErrorMessage', `CSS file not found. Error: ${err}`);
                    });
              }
            }
        );

        this.watch(
            () => getters.backgroundStoragePath,
            url => {
              if (url) {
                // fetch custom background image URL
                storage.then(s => s.ref().child(url).getDownloadURL())
                    .then(url => {
                      commit('setBackgroundFile', url);
                    })
                    .catch(err => {
                      log.error('error getting background URL', err);
                      commit('setMinorErrorMessage', `CSS file not found. Error: ${err}`);
                    });
              }
            }
        );

        // watch for changes to stylesheet and add to doc
        this.watch(
            () => getters.style,
            cssFile => {
              if (document && cssFile) {
                let link = document.getElementById('global-style');
                if (link) {
                  link.href = cssFile;
                } else {
                  link = document.createElement('link');
                  link.rel = 'stylesheet';
                  link.href = cssFile;
                  link.id = 'global-style';
                  document.head.appendChild(link);
                }
              } else {
                log.debug('no css file to add');
              }
            },
            {immediate: true}
        );
      }
    },
    unbind({commit}) {
      commit('reset');
    }
  }
};
